import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile 
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';

// Konstante für sichere Ablaufzeiten (Token-Lebensdauer)
const TOKEN_EXPIRATION = 24 * 60 * 60 * 1000; // 24 Stunden in Millisekunden
const PORTAL_ACCESS_DENIED_MESSAGE = 'Dieses Konto hat keinen Zugriff auf das Admin-Portal. Fahrer melden sich bitte in der Fahrer-App an.';

export const PORTAL_ACCESS_ROLES = [
  'admin',
  'dispatcher',
  'central_admin',
  'central_dispatch',
  'operator_admin',
  'operator_staff'
];

export const hasPortalAccess = (userData) => {
  if (!userData || userData.isDriver === true) {
    return false;
  }

  return PORTAL_ACCESS_ROLES.includes(userData.role);
};

export const isCentralRole = (userData) => (
  userData?.role === 'admin' ||
  userData?.role === 'super_admin' ||
  userData?.role === 'central_admin' ||
  userData?.role === 'central_dispatch' ||
  userData?.isAdmin === true
);

export const isOperatorRole = (userData) => (
  userData?.role === 'operator_admin' ||
  userData?.role === 'operator_staff'
);

const buildAuthUser = (firebaseUser, userData, overrides = {}) => {
  const displayName = firebaseUser?.displayName || userData?.name || firebaseUser?.email || '';
  const photoURL = firebaseUser?.photoURL || userData?.profilePic || userData?.profile_picture || '';

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName,
    name: userData?.name || displayName,
    photoURL,
    role: userData.role,
    permissions: userData.permissions,
    organizationId: userData.organizationId || null,
    operatorId: userData.operatorId || null,
    operatorIds: userData.operatorIds || (userData.operatorId ? [userData.operatorId] : []),
    isDriver: userData.isDriver === true,
    token_expiry: Date.now() + TOKEN_EXPIRATION,
    ...overrides
  };
};

// Async thunks
export const registerUser = createAsyncThunk(
  'auth/register',
  async ({ email, password, name, phone, role = 'dispatcher' }, { rejectWithValue }) => {
    try {
      // Passwort-Sicherheit prüfen
      if (password.length < 8) {
        return rejectWithValue('Passwort muss mindestens 8 Zeichen lang sein und sollte Sonderzeichen, Zahlen und Groß-/Kleinbuchstaben enthalten.');
      }
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, {
        displayName: name
      });

      // Speichern der Benutzerinformationen in Firestore
      // Achtung: Nur notwendige Daten speichern (DSGVO-konform)
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email,
        name,
        phone,
        role,
        created_at: new Date().toISOString(),
        last_login: new Date().toISOString(),
        profile_picture: '',
        active: true,
        permissions: [],
        token_expiry: Date.now() + TOKEN_EXPIRATION
      });

      return {
        uid: user.uid,
        email: user.email,
        displayName: name,
        role
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Mehrere Versuche für Firestore-Anfrage bei instabiler Verbindung
      let userDoc = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          userDoc = await getDoc(doc(db, 'users', user.uid));
          break; // Wenn erfolgreich, Schleife beenden
        } catch (error) {
          // Verbindungsfehler behandeln
          console.warn(`Firestore-Verbindungsfehler beim Abrufen des Benutzerprofils (Versuch ${retryCount + 1}/${maxRetries}):`, error);
          
          // Kurze Verzögerung vor dem nächsten Versuch
          await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
          retryCount++;
          
          if (retryCount >= maxRetries) {
            // Maximale Anzahl an Versuchen erreicht
            return rejectWithValue('Fehler beim Abrufen des Benutzerprofils nach mehreren Versuchen. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.');
          }
        }
      }
      
      // Prüfen ob Benutzerdokument existiert
      if (!userDoc.exists()) {
        return rejectWithValue('Benutzerkonto existiert nicht in der Datenbank');
      }
      
      const userData = userDoc.data();

      if (!hasPortalAccess(userData)) {
        await signOut(auth);
        return rejectWithValue(PORTAL_ACCESS_DENIED_MESSAGE);
      }

      // Rollenbasierte Zugriffssteuerung (RBAC)
      // Admin-Status über Rolle bestimmen, nicht über E-Mail-Domain
      const isAdmin = userData.role === "admin";

      // Update last login und Token-Ablaufzeit mit Wiederholungsversuch
      let updateSuccess = false;
      retryCount = 0;
      
      while (retryCount < maxRetries && !updateSuccess) {
        try {
          await setDoc(doc(db, 'users', user.uid), {
            ...userData,
            last_login: new Date().toISOString(),
            token_expiry: Date.now() + TOKEN_EXPIRATION,
            isAdmin: isAdmin,
          }, { merge: true });
          updateSuccess = true;
        } catch (error) {
          console.warn(`Fehler beim Aktualisieren der Benutzerdaten (Versuch ${retryCount + 1}/${maxRetries}):`, error);
          
          // Kurze Verzögerung vor dem nächsten Versuch
          await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
          retryCount++;
          
          if (retryCount >= maxRetries) {
            // Wir setzen trotzdem fort, da der Benutzer authentifiziert ist
            console.error('Maximale Anzahl an Versuchen erreicht, Login-Datum wird nicht aktualisiert');
          }
        }
      }

      // Protokollierung nur in Entwicklungsumgebung
      if (process.env.NODE_ENV === 'development') {
        console.log(`Benutzer eingeloggt, Rolle: ${userData.role}`);
      }

      return buildAuthUser(user, userData, { isAdmin });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkAuthState = createAsyncThunk(
  'auth/checkState',
  async (_, { rejectWithValue }) => {
    try {
      return new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          unsubscribe();
          if (user) {
            try {
              // Mehrere Versuche für Firestore-Anfrage bei instabiler Verbindung
              let userDoc = null;
              let retryCount = 0;
              const maxRetries = 3;
              
              while (retryCount < maxRetries) {
                try {
                  userDoc = await getDoc(doc(db, 'users', user.uid));
                  break; // Wenn erfolgreich, Schleife beenden
                } catch (error) {
                  // Verbindungsfehler behandeln
                  console.warn(`Firestore-Verbindungsfehler beim Abrufen des Benutzerprofils (Versuch ${retryCount + 1}/${maxRetries}):`, error);
                  
                  // Kurze Verzögerung vor dem nächsten Versuch
                  await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
                  retryCount++;
                  
                  if (retryCount >= maxRetries) {
                    // Maximale Anzahl an Versuchen erreicht
                    console.error('Maximale Anzahl an Versuchen erreicht, Benutzer wird nicht authentifiziert');
                    resolve(null);
                    return;
                  }
                }
              }
              
              // Prüfen ob Benutzerdokument existiert
              if (!userDoc.exists()) {
                resolve(null);
                return;
              }
              
              const userData = userDoc.data();

              if (!hasPortalAccess(userData)) {
                await signOut(auth);
                resolve(null);
                return;
              }
              
              // Prüfen ob Token abgelaufen ist
              if (userData.token_expiry && userData.token_expiry < Date.now()) {
                // Automatisch abmelden bei abgelaufenem Token
                await signOut(auth);
                resolve(null);
                return;
              }
              
              // Token-Ablaufzeit aktualisieren
              try {
                await setDoc(doc(db, 'users', user.uid), {
                  ...userData,
                  token_expiry: Date.now() + TOKEN_EXPIRATION
                }, { merge: true });
              } catch (updateError) {
                console.warn('Fehler beim Aktualisieren der Token-Ablaufzeit:', updateError);
                // Wir setzen trotzdem fort, da der Benutzer authentifiziert ist
              }
              
              resolve(buildAuthUser(user, userData, {
                isAdmin: userData.role === "admin"
              }));
            } catch (innerError) {
              console.error('Fehler bei der Authentifizierungsprüfung:', innerError);
              resolve(null);
            }
          } else {
            resolve(null);
          }
        });
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Check auth state
      .addCase(checkAuthState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = !!action.payload;
        state.user = action.payload;
      })
      .addCase(checkAuthState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const { resetAuthError } = authSlice.actions;
export const selectCurrentUser = (state) => state.auth.user;
export const selectIsCentralAdmin = (state) => isCentralRole(state.auth.user);
export const selectIsOperatorAdmin = (state) => state.auth.user?.role === 'operator_admin';
export const selectCurrentOperatorId = (state) => state.auth.user?.operatorId || null;
export const selectCanManageOperators = (state) => isCentralRole(state.auth.user);
export const selectCanManageGlobalSettings = (state) => isCentralRole(state.auth.user);
export default authSlice.reducer;
